import { faCarWrench, faCircleNotch } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Stack, TextField, Typography } from "@mui/material"
import { useApiDelete, useApiFile } from "auth/useApi2"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ContinueDialog from "components/Dialogs/ContinueDialog"
import SuccessDialog from "components/Dialogs/SuccessDialog"
import FormUploadFile from "components/Form/FormComponents/FormUploadFile/FormUploadFile"
import FormUploadPreviewList from "components/Form/FormComponents/FormUploadFile/FormUploadPreviewList"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import theme from "css/theme/light"
import { useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useRef, useState } from "react"
import { StyledAutocomplete, StyledListItem, StyledListItemText } from "shared/styled/General"
import * as Yup from "yup"
import ReadMoreReadLess from "./ReadMoreReadLess"
import { ReadMoreReadLessTitle } from "./ReadMoreReadLessTitle"
import { Sidebar } from "components/sidedrawer/Sidebar2"
import { useTranslation } from "react-i18next"
import InfoTooltip from "components/Tooltip/InfoTooltip"

const IssueReportUploadFile = ({ filesPreview, uploadFile, handleRemove, fileUploadResult, t }) => {
  return (
    <StyledListItem>
      <StyledListItemText style={{ paddingBottom: "8px" }} primary={t("global.images", "Images")} />
      <Box width="100%" textAlign="center">
        <FormUploadFile
          acceptedFileFormat={{
            "image/jpeg": [".jpg", ".jpeg"],
            "image/svg+xml": [".svg"],
            "image/png": [".png"]
          }}
          handleChange={(files) => {
            files.forEach((file) => {
              uploadFile(apiEndpoints.issuereports.uploadfile, file)
            })
          }}
        />
        <FormUploadPreviewList
          fileUploadResult={fileUploadResult}
          handleRemove={(index, file) => handleRemove(index, file)}
          files={filesPreview}
        />
      </Box>
    </StyledListItem>
  )
}

const IssueReportDescriptionTextField = ({ t }) => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext()

  return (
    <StyledListItem>
      <StyledListItemText style={{ paddingBottom: "8px" }} primary={t("global.description", "Description")} />
      <TextField
        placeholder={t("issuereports.describe_the_issue", "Describe the issue as thoroughly as possible")}
        rows={4}
        multiline
        autoComplete="off"
        fullWidth
        id="description"
        value={values.description}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.description && errors.description ? true : false}
        helperText={touched.description && errors.description ? errors.description : null}
        variant="outlined"
        InputProps={{
          disableUnderline: true
        }}
      />
    </StyledListItem>
  )
}

const IssueReportCategorySelect = ({
  categorysResult,
  setChosenCategory,
  vehicleCategoriesResult,
  chosenRegNumber,
  issueReports,
  chosenCategory,
  t
}) => {
  const { values, handleChange, handleBlur, touched, errors, setFieldValue } = useFormikContext()

  const categorysResultInAlphabeticalOrder = categorysResult.data
    ? [...categorysResult.data].filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name))
    : []

  return (
    <>
      <StyledListItem>
        <StyledListItemText style={{ paddingBottom: "8px" }} primary={t("global.category", "Category")} />
        {categorysResult.status === 2 && (
          <StyledAutocomplete
            id="issueReportCategoryId"
            options={categorysResultInAlphabeticalOrder}
            getOptionLabel={(option) => (option.name ? option.name : "")}
            value={categorysResult.data.find((i) => i.id === values.issueReportCategoryId) || ""}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id
            }}
            onChange={(e, value) => {
              setFieldValue("issueReportCategoryId", value?.id)
              setChosenCategory(value?.id)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                name="issueReportCategoryId"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.issueReportCategoryId && errors.issueReportCategoryId ? true : false}
                helperText={
                  touched.issueReportCategoryId && errors.issueReportCategoryId
                    ? errors.issueReportCategoryId
                    : null
                }
              />
            )}
          />
        )}
      </StyledListItem>
      {chosenRegNumber && chosenCategory && (
        <Box>
          <>
            {vehicleCategoriesResult.status === 2 ? (
              issueReports.length > 0 ? (
                <>
                  <ReadMoreReadLessTitle t={t} />
                  {issueReports.map((data) => (
                    <ReadMoreReadLess key={data.id} date={data.createdDate} description={data.description} />
                  ))}
                </>
              ) : (
                <Typography mt={0} mb={2} fontSize="13px" color="success.main">
                  {t("issuereports.there_is_no_existing_issuereports", "There is no existing issue reports")}
                </Typography>
              )
            ) : (
              <FontAwesomeIcon
                spin
                icon={faCircleNotch}
                size="1x"
                variant="contained"
                color={theme.palette.primary["main"]}
              />
            )}
          </>
        </Box>
      )}
    </>
  )
}

const IssueReportMeterSettingTextField = ({ t }) => {
  const { values, handleChange, handleBlur } = useFormikContext()

  return (
    <StyledListItem>
      <StyledListItemText
        style={{ paddingBottom: "8px", display: "flex", gap: "4px", alignItems: "center" }}
        primary={t("global.meter_setting", "Meter setting")}
        secondary={
          <InfoTooltip
            title={t(
              "global.this_field_is_prefilled_with_metersetting",
              "This field is pre-filled with the last known meter reading from the system. Update the value if you know the exact figure at the time of the injury."
            )}
          />
        }
      />
      <TextField
        type="number"
        minRows={1}
        autoComplete="off"
        fullWidth
        id="meterSetting"
        value={values?.meterSetting}
        onChange={handleChange}
        onBlur={handleBlur}
        variant="outlined"
        InputProps={{
          disableUnderline: true,
          inputProps: { min: 0 }
        }}
      />
    </StyledListItem>
  )
}

const IssueReportVehicleSelect = ({
  vehiclesResult,
  chosenVehicle,
  setChosenVehicle,
  setChosenRegNumber,
  t
}) => {
  const { values, handleChange, handleBlur, touched, errors, setFieldValue } = useFormikContext()
  const filteredVehicles = vehiclesResult.data?.filter((vehicle) => !vehicle.archived) || []
  return (
    <StyledListItem>
      <StyledListItemText
        style={{ paddingBottom: "8px" }}
        primary={t("global.select_vehicle", "Select vehicle")}
      />
      <StyledAutocomplete
        id="vehicleId"
        options={filteredVehicles}
        getOptionLabel={(option) => {
          return chosenVehicle
            ? `${chosenVehicle.regNumber} (${chosenVehicle.internnumber})`
            : option.regNumber
            ? `${option.regNumber} (${option.internnumber})`
            : ""
        }}
        value={filteredVehicles.find((i) => i.id === values.vehicleId) || ""}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id
        }}
        onChange={(e, value) => {
          if (value === null) {
            setFieldValue("vehicleId", null)
            setFieldValue("companyId", null)
            setChosenRegNumber && setChosenRegNumber("")
            setChosenVehicle && setChosenVehicle(null)
          } else {
            setFieldValue("vehicleId", value?.id)
            setFieldValue("companyId", value?.companyId)
            setChosenRegNumber(value?.id)
          }
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Box sx={{ fontWeight: 600, minWidth: "100px" }}>{option.regNumber}</Box>
              <Box sx={{ opacity: 0.6 }}>{option.internnumber}</Box>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            name="vehicleId"
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.vehicleId && errors.vehicleId ? true : false}
            helperText={touched.vehicleId && errors.vehicleId ? errors.vehicleId : null}
          />
        )}
      />
    </StyledListItem>
  )
}

// validation
const currentValidationSchema = (t) =>
  Yup.object().shape({
    vehicleId: Yup.string().required(t("global.vehicle_is_required", "Vehicle is required")),
    issueReportCategoryId: Yup.string().required(t("global.category_is_required", "Category is required")),
    description: Yup.string()
      .required(t("global.description_is_required", "Description is required"))
      .max(300, t("global.description_can_only_300", "Description can be a maximum of 300 characters long"))
  })

const ImportIssueReportForm = ({
  showSidebarForm,
  setShowSidebarForm,
  updateExistingData,
  chosenVehicle,
  setChosenVehicle
}) => {
  // formik initialvalues
  const initialValues = {
    vehicleId: null,
    companyId: null,
    issueReportCategoryId: "",
    description: "",
    attachments: [],
    meterSetting: null
  }
  const appContext = useContext(AppContext)

  // datafetch states and handlers
  const [deleteResult, deleteFile, resetDeleteState] = useApiDelete()
  const [vehiclesResult, vehiclesApi] = useApi()
  const [categorysResult, categoryApi] = useApi()
  const [vehicleCategoriesResult, vehicleCategoriesApi] = useApi()
  const [addResult, addApi] = useApi()
  const [fileUploadResult, uploadFile] = useApiFile()
  const [files, setFiles] = useState([])
  const [filesPreview, setFilesPreview] = useState(initialValues.attachments)
  const [chosenCategory, setChosenCategory] = useState("")
  const [issueReports, setIssueReports] = useState([])
  const [showContinueModal, setShowContinueModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [chosenRegNumber, setChosenRegNumber] = useState("")
  const formRef = useRef()
  const [initialFormikValues, setInitialFormikValues] = useState(initialValues)
  const { t } = useTranslation()

  useEffect(() => {
    if (vehiclesResult.status === 2 && chosenRegNumber) {
      const meterSettingForChosenRegNumber = vehiclesResult?.data.find(
        (v) => v.id === chosenRegNumber
      )?.meterSetting
      formRef.current.setFieldValue("meterSetting", meterSettingForChosenRegNumber || 0)
    }
  }, [chosenRegNumber])

  useEffect(() => {
    if (chosenVehicle && formRef.current !== undefined) {
      setChosenRegNumber(chosenVehicle.id)
    }
  }, [chosenVehicle])

  useEffect(() => {
    if (appContext.isSidebarOpen && formRef.current && chosenVehicle) {
      const meterSetting =
        chosenVehicle?.meterSetting !== null && chosenVehicle?.meterSetting > 0
          ? chosenVehicle.meterSetting
          : null
      const newVehicleId = chosenVehicle.id
      const newCompanyId = chosenVehicle.companyId

      formRef.current.setFieldValue("vehicleId", newVehicleId)
      formRef.current.setFieldValue("meterSetting", meterSetting || 0)
      formRef.current.setFieldValue("companyId", newCompanyId)
    }
  }, [appContext.isSidebarOpen, chosenVehicle])

  /* GET Categories */
  useEffect(() => {
    categoryApi.get(apiEndpoints.issuereports.getissuereportcategories)
    return () => {
      categoryApi.leave()
    }
  }, [categoryApi])

  /* File management */
  useEffect(() => {
    if (files) {
      files.map((file) => {
        return uploadFile(apiEndpoints.issuereports.uploadfile, file)
      })
    }
  }, [files, uploadFile])

  useEffect(() => {
    if (fileUploadResult.data && fileUploadResult.data.name) {
      setFilesPreview((p) => {
        return [...p, fileUploadResult.data]
      })
    }
  }, [fileUploadResult])

  /* Show images in a list */
  useEffect(() => {
    if (addResult.status === 2) {
      categoryApi.get(apiEndpoints.issuereports.getissuereportcategories)
      if (updateExistingData !== null) {
        updateExistingData()
      }
    }
  }, [addResult])

  useEffect(() => {
    if (addResult.status === 2) {
      setShowContinueModal(false)
      setShowSuccessModal(true)

      appContext.toggleSideBar(false)
    }
  }, [addResult])

  // loading
  const loadingContext = useContext(GlobalLoaderContext)
  useEffect(() => {
    loadingContext.setLoading(() => vehiclesResult.status === 1)
    loadingContext.setLoading(() => categorysResult.status === 1)
    loadingContext.setLoading(() => addResult.status === 1)
    loadingContext.setLoading(() => fileUploadResult.status === 1)
  })

  // handle submit formik form & show continue modal
  const handleSubmit = () => {
    setShowContinueModal(true)
  }

  // post data & set states for modals/sidebar
  const onAddSubmit = (values) => {
    const meterSetting = values.meterSetting === "" || values.meterSetting === undefined ? null : Number(values.meterSetting)
    const payload = {
      ...values,
      meterSetting
    }
    addApi.post(apiEndpoints.issuereports.createissuereport, payload)
  }

  // clear form when sidebar is closed
  useEffect(() => {
    if (!appContext.isSidebarOpen && formRef.current !== undefined) {
      formRef?.current?.resetForm()
      setChosenRegNumber("")
      setChosenCategory("")
      setFilesPreview([])
    } else {
      vehiclesApi.get(apiEndpoints.issuereports.getvehiclelist)
    }
  }, [appContext.isSidebarOpen])

  useEffect(() => {
    if (chosenRegNumber && chosenCategory) {
      vehicleCategoriesApi.get(
        apiEndpoints.issuereports.getissuereportsbyvehicleidandcategoryid
          .replace("{vehicleid}", chosenRegNumber)
          .replace("{issuereportcategoryid}", chosenCategory)
      )
    }
  }, [chosenCategory, chosenRegNumber, vehicleCategoriesApi])

  useEffect(() => {
    if (vehicleCategoriesResult.status === 2) {
      setIssueReports(vehicleCategoriesResult.data)
    } else {
      setIssueReports([])
    }
  }, [vehicleCategoriesResult])

  useEffect(() => {
    if (filesPreview && formRef.current !== undefined) {
      formRef.current.setFieldValue("attachments", filesPreview)
    }
  }, [filesPreview])

  const handleRemove = (attachments, index) => {
    if (attachments) {
      deleteFile(apiEndpoints.issuereports.deletefile.replace("{id}", attachments.id))

      const newPreviewFiles = [...filesPreview]
      newPreviewFiles.splice(index, 1)
      setFilesPreview(newPreviewFiles)

      const newFiles = [...files]
      newFiles.splice(index, 1)
      setFiles(newFiles)
      formRef.current.setFieldValue("attachments", newFiles)
    }
  }

  return (
    <>
      <Sidebar
        validationSchema={currentValidationSchema(t)}
        initialFormikValues={initialValues}
        handleSubmit={handleSubmit}
        setInitialFormikValues={setInitialFormikValues}
        formResult={issueReports}
        formRef={formRef}
        formData={{
          formId: "CustomFieldForm",
          titleEdit: t("issuereports.create_issuereport", "Create issue report"),
          subTitleEdit: t("global.vehicle", "Vehicle"),
          submitText: t("issuereports.send_issuereport", "Send issue report"),
          icon: faCarWrench
        }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <IssueReportVehicleSelect
            t={t}
            vehiclesResult={vehiclesResult}
            chosenVehicle={chosenVehicle}
            setChosenVehicle={setChosenVehicle}
            setChosenRegNumber={setChosenRegNumber}
          />

          <IssueReportMeterSettingTextField t={t} />

          <IssueReportCategorySelect
            t={t}
            categorysResult={categorysResult}
            setChosenCategory={setChosenCategory}
            vehicleCategoriesResult={vehicleCategoriesResult}
            chosenRegNumber={chosenRegNumber}
            issueReports={issueReports}
            chosenCategory={chosenCategory}
          />

          <IssueReportDescriptionTextField t={t} />

          <IssueReportUploadFile
            t={t}
            filesPreview={filesPreview}
            uploadFile={uploadFile}
            handleRemove={handleRemove}
            fileUploadResult={fileUploadResult}
          />

          {showContinueModal && (
            <ContinueDialog
              result={addResult}
              setShowContinueModal={setShowContinueModal}
              onAddSubmit={onAddSubmit}
              values={formRef.current.values}
              message={t(
                "issuereports.you_are_about_to_create_and_submit",
                "You are about to create and submit a issue report, do you want to continue?"
              )}
              continueBtnText={t("issuereports.send_issuereport", "Send issue report")}
              cancelBtnText={t("global.cancel", "Cancel")}
            />
          )}

          {showSuccessModal && (
            <SuccessDialog
              resetModalState={setShowSuccessModal}
              title={t(
                "issuereports.saved_and_create_case",
                "Your issue report has been saved and a case will be created. You will receive a notification about the case status when it is updated."
              )}
            />
          )}
        </Box>
      </Sidebar>
    </>
  )
}
export default ImportIssueReportForm
